import { useContext } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { Plus } from '@phosphor-icons/react'

import { Button, Text, ToastContext } from '@reveni-io/reveni-ui'

import SaveChangesButtons from '@/common/components/SaveChangesButtons/SaveChangesButtons'
import UnsavedChangesAlert from '@/common/components/UnsavedChangesAlert/UnsavedChangesAlert'
import { userPermissions } from '@/common/constants'
import AppContext from '@/common/context/app.context'
import { getReasonLabel } from '@/common/helpers/helpers'
import useDragSort from '@/common/hooks/useDragSort/useDragSort'
import useUserPermissions from '@/common/hooks/useUserPermissions/useUserPermissions'
import routes from '@/Router/routes.constants'

import DeleteReturnReasonModal from './DeleteReturnReasonModal/DeleteReturnReasonModal'
import useReturnReasonsConfiguration from './hooks/useReturnReasonsConfiguration/useReturnReasonsConfiguration'
import ReturnReasonsConfigurationSkeleton from './ReturnReasonsConfigurationSkeleton/ReturnReasonsConfigurationSkeleton'
import SortableReturnReasonList from './SortableReturnReasonList/SortableReturnReasonList'
import * as Styles from './styles'

const ReturnReasonsConfiguration = () => {
  const { t, i18n } = useTranslation()
  const { info } = useContext(AppContext)
  const { setToastInfo } = useContext(ToastContext)
  const navigate = useNavigate()
  const {
    reasons,
    setReasons,
    fetchingReturnPortalFeatures,
    gettingReasons,
    hasChanges,
    revertChanges,
    editingReturnReasons,
    reorderReasons,
    showDeleteReturnReasonModal,
    handleDelete,
    handleOnOpenDeleteDeliveryMethodModal,
    handleCloseDeleteDeliveryMethodModal,
    onAddNewReturnReason,
    returnPortalFeatures,
    onSave,
  } = useReturnReasonsConfiguration({
    setToastInfo,
    returnPortalId: info.selectedReturnPortal?.id,
  })

  const { hasPermission } = useUserPermissions()
  const { dragging, onSortStart, onSortEnd } = useDragSort(reorderReasons)

  const onChangeEnabled = (reason, checked) => {
    setReasons(
      reasons.map(r => {
        if (r.id === reason.id) {
          return { ...r, is_enabled: checked }
        }
        return r
      })
    )
  }

  const onEdit = reason => {
    const reasonName = getReasonLabel(reason, i18n.language)

    navigate(`${routes.configurationPage.returnReasons}/${reason.id}`, {
      state: { childPageTitle: reasonName, selectedReturnReason: reason },
    })
  }

  if (fetchingReturnPortalFeatures || gettingReasons) {
    return <ReturnReasonsConfigurationSkeleton />
  }

  return (
    <>
      <Styles.Container>
        {hasChanges && <UnsavedChangesAlert hasPermission={hasPermission(userPermissions.editReturnReasons)} />}

        <Styles.Section>
          <Text type="titleM">{t('Motivos devolución')}</Text>
          <Text type="bodyMRegular">
            {t('Define los motivos que los clientes podrán escoger a la hora de devolver o cambiar un producto')}:
          </Text>
          <div>
            <SortableReturnReasonList
              reasons={reasons}
              onSortEnd={onSortEnd}
              onSortStart={onSortStart}
              dragging={dragging}
              onChangeEnabled={onChangeEnabled}
              onDelete={handleOnOpenDeleteDeliveryMethodModal}
              onEdit={onEdit}
              isEditable={hasPermission(userPermissions.editReturnReasons)}
              returnReasonsFiltering={returnPortalFeatures?.return_reasons_filtering}
            />
          </div>
          <div>
            {hasPermission(userPermissions.editReturnReasons) && (
              <Button variant="text" onClick={onAddNewReturnReason} size="m">
                <Plus /> {t('Añadir nuevo motivo')}
              </Button>
            )}
          </div>
        </Styles.Section>
        <SaveChangesButtons
          hasChanges={hasPermission(userPermissions.editReturnReasons) && hasChanges}
          onSave={onSave}
          onRevertChanges={revertChanges}
          loading={editingReturnReasons}
          disabled={editingReturnReasons}
        />
      </Styles.Container>
      <DeleteReturnReasonModal
        onDelete={handleDelete}
        show={showDeleteReturnReasonModal}
        onClose={handleCloseDeleteDeliveryMethodModal}
      />
    </>
  )
}

export default ReturnReasonsConfiguration
